/**
 * SiteInfoModal
 *
 * Modal dialog showing detailed site/location information:
 * - Header with site name, customer, and address
 * - Statistics row (patrols in last 30 days, completion rate)
 * - Today's patrols section
 * - Patrol history with "Load More" pagination
 *
 * Opens when clicking "Site Info" button in PatrolCircleInfoWindow.
 */

import React, { useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import HistoryIcon from '@mui/icons-material/History';
import TodayIcon from '@mui/icons-material/Today';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import classNames from 'classnames';
import styles from './SiteInfoModal.module.css';
import { useSitePatrols } from '../../hooks/dispatch/useSitePatrols';
import { SitePatrolHistoryItem } from '../../../../../api/dispatch/types';
import { PatrolStatus } from './types';

// ============================================
// Types
// ============================================

/** Data passed when viewing patrol details */
export interface PatrolDetailData {
  scheduleAssignmentId: number;
  guardName: string;
  siteName: string;
  patrolStatus: PatrolStatus;
  startedAt: string | null;
}

export interface SiteInfoModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** The address/site ID to display */
  addressId: number | null;
  /** Initial site name (from circle data, before API loads) */
  initialSiteName?: string;
  /** Initial customer name (from circle data, before API loads) */
  initialCustomerName?: string;
  /** Callback when user clicks a patrol row to view details */
  onViewPatrolDetails?: (data: PatrolDetailData) => void;
  /** Callback when user clicks "Schedule Patrol" to navigate to the scheduler */
  onSchedulePatrol?: (addressId: number | null, siteName: string, customerName: string) => void;
}

// ============================================
// Helper Functions
// ============================================

/** Status icons for patrol rows */
const STATUS_ICONS: Record<PatrolStatus, React.ElementType> = {
  completed: CheckCircleIcon,
  in_progress: PlayArrowIcon,
  pending: ScheduleIcon,
};

/** Status colors for chips */
const STATUS_COLORS: Record<PatrolStatus, 'success' | 'primary' | 'default'> = {
  completed: 'success',
  in_progress: 'primary',
  pending: 'default',
};

/** Status labels */
const STATUS_LABELS: Record<PatrolStatus, string> = {
  completed: 'Completed',
  in_progress: 'In Progress',
  pending: 'Pending',
};

/** Format time for display (e.g., "8:00 AM") */
function formatTime(isoString: string | null): string {
  if (!isoString) return '';
  try {
    return new Date(isoString).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

/** Format date for display (e.g., "Jan 30") */
function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

// ============================================
// Sub-components
// ============================================

interface PatrolRowProps {
  patrol: SitePatrolHistoryItem;
  onClick?: () => void;
  showDate?: boolean;
}

/**
 * Patrol row component for the patrol list
 */
const PatrolRow: React.FC<PatrolRowProps> = ({ patrol, onClick, showDate = false }) => {
  // Guard against null/undefined patrol
  if (!patrol) {
    return null;
  }

  const status = patrol.status ?? 'pending';
  const StatusIcon = STATUS_ICONS[status] ?? ScheduleIcon;

  return (
    <Box
      className={classNames(styles.patrolRow, { [styles.clickable]: !!onClick })}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      <Box className={classNames(styles.statusIcon, styles[status])}>
        <StatusIcon fontSize="small" />
      </Box>
      <Box className={styles.patrolContent}>
        <Typography className={styles.guardName}>{patrol.guardName ?? 'Unknown Guard'}</Typography>
        <Box className={styles.patrolMeta}>
          {showDate && patrol.workDate && (
            <span className={styles.date}>{formatDate(patrol.workDate)}</span>
          )}
          {patrol.actualStart && (
            <span className={styles.time}>
              {formatTime(patrol.actualStart)}
              {patrol.actualEnd ? ` - ${formatTime(patrol.actualEnd)}` : ' -'}
            </span>
          )}
          {!patrol.actualStart && patrol.scheduledStart && (
            <span className={styles.time}>
              Scheduled: {formatTime(patrol.scheduledStart)}
            </span>
          )}
          {(patrol.checkpointsTotal ?? 0) > 0 && (
            <span className={styles.checkpoints}>
              {patrol.checkpointsCompleted ?? 0}/{patrol.checkpointsTotal} checkpoints
            </span>
          )}
        </Box>
      </Box>
      {onClick && <ChevronRightIcon className={styles.chevron} />}
    </Box>
  );
};

// ============================================
// Main Component
// ============================================

/**
 * Site information modal with patrol history
 */
const SiteInfoModal: React.FC<SiteInfoModalProps> = ({
  open,
  onClose,
  addressId,
  initialSiteName,
  initialCustomerName,
  onViewPatrolDetails,
  onSchedulePatrol,
}) => {
  const {
    siteName,
    customerName,
    fullAddress,
    stats,
    todaysPatrols,
    history,
    hasMore,
    isLoading,
    isFetchingNextPage,
    isError,
    error,
    fetchNextPage,
    refetch,
  } = useSitePatrols({
    addressId,
    enabled: open && addressId !== null,
  });

  // Use initial values while loading (must be before handlePatrolClick which uses displaySiteName)
  const displaySiteName = siteName || initialSiteName || 'Unknown Site';
  const displayCustomerName = customerName || initialCustomerName || '';

  // Handle patrol row click
  const handlePatrolClick = useCallback(
    (patrol: SitePatrolHistoryItem) => {
      onViewPatrolDetails?.({
        scheduleAssignmentId: patrol.patrolId, // patrolId in SitePatrolHistoryItem is the scheduleAssignmentId
        guardName: patrol.guardName,
        siteName: displaySiteName,
        patrolStatus: patrol.status,
        startedAt: patrol.actualStart,
      });
    },
    [onViewPatrolDetails, displaySiteName]
  );

  // Handle load more
  const handleLoadMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  // Ensure arrays are safe to iterate
  const safeTodaysPatrols = Array.isArray(todaysPatrols) ? todaysPatrols : [];
  const safeHistory = Array.isArray(history) ? history : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* Header */}
      <DialogTitle className={styles.dialogTitle}>
        <Box className={styles.titleContent}>
          <LocationOnIcon color="primary" />
          <Box className={styles.titleText}>
            <Typography className={styles.siteName}>{displaySiteName}</Typography>
            {displayCustomerName && (
              <Typography className={styles.customerName}>
                <BusinessIcon fontSize="small" />
                {displayCustomerName}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.dialogContent} dividers>
        {/* Loading */}
        {isLoading && (
          <Box className={styles.loadingContainer}>
            <CircularProgress size={32} />
            <Typography color="textSecondary">Loading site data...</Typography>
          </Box>
        )}

        {/* Error */}
        {isError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            {error?.message || 'Failed to load site data'}
          </Alert>
        )}

        {/* Content */}
        {!isLoading && !isError && (
          <>
            {/* Full Address */}
            {fullAddress && (
              <Typography className={styles.fullAddress} color="textSecondary">
                {fullAddress}
              </Typography>
            )}

            {/* Stats */}
            {stats && (
              <Box className={styles.statsRow}>
                <Box className={styles.statItem}>
                  <Typography className={styles.statValue}>
                    {stats.totalPatrols30Days}
                  </Typography>
                  <Typography className={styles.statLabel}>Patrols (30d)</Typography>
                </Box>
                <Box className={styles.statItem}>
                  <Typography className={styles.statValue}>
                    {stats.completedPatrols30Days}
                  </Typography>
                  <Typography className={styles.statLabel}>Completed</Typography>
                </Box>
                <Box className={styles.statItem}>
                  <Typography className={styles.statValue}>
                    {stats.totalPatrols30Days > 0
                      ? Math.round(
                          (stats.completedPatrols30Days / stats.totalPatrols30Days) * 100
                        )
                      : 0}%
                  </Typography>
                  <Typography className={styles.statLabel}>Completion</Typography>
                </Box>
              </Box>
            )}

            <Divider />

            {/* Today's Patrols */}
            <Box className={styles.section}>
              <Typography className={styles.sectionTitle}>
                <TodayIcon fontSize="small" />
                Today's Patrols
                <Chip
                  label={safeTodaysPatrols.length}
                  size="small"
                  className={styles.countChip}
                />
              </Typography>
              {safeTodaysPatrols.length > 0 ? (
                <Box className={styles.patrolList}>
                  {safeTodaysPatrols.map((patrol) => (
                    <PatrolRow
                      key={patrol.patrolId}
                      patrol={patrol}
                      onClick={
                        onViewPatrolDetails
                          ? () => handlePatrolClick(patrol)
                          : undefined
                      }
                    />
                  ))}
                </Box>
              ) : (
                <Typography className={styles.emptyText}>
                  No patrols scheduled for today
                </Typography>
              )}
            </Box>

            <Divider />

            {/* Patrol History */}
            <Box className={styles.section}>
              <Typography className={styles.sectionTitle}>
                <HistoryIcon fontSize="small" />
                Patrol History
              </Typography>
              {safeHistory.length > 0 ? (
                <>
                  <Box className={styles.patrolList}>
                    {safeHistory.map((patrol) => (
                      <PatrolRow
                        key={patrol.patrolId}
                        patrol={patrol}
                        showDate
                        onClick={
                          onViewPatrolDetails
                            ? () => handlePatrolClick(patrol)
                            : undefined
                        }
                      />
                    ))}
                  </Box>
                  {hasMore && (
                    <Button
                      onClick={handleLoadMore}
                      disabled={isFetchingNextPage}
                      fullWidth
                      className={styles.loadMoreButton}
                    >
                      {isFetchingNextPage ? 'Loading...' : 'Load More'}
                    </Button>
                  )}
                </>
              ) : (
                <Typography className={styles.emptyText}>No patrol history</Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        {onSchedulePatrol && (
          <Button
            onClick={() => onSchedulePatrol(addressId, displaySiteName, displayCustomerName)}
            variant="contained"
            color="primary"
            startIcon={<CalendarTodayIcon />}
          >
            Schedule Patrol
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SiteInfoModal;
